import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { filter, interval, Observable, Subject, Subscription, switchMap, take, takeUntil, tap } from 'rxjs';
import { RibaModalService, RibaModalData } from '../../services/riba-modal.service';
import { TypesenseService } from '../../services/typesense.service';
import { CrowdfundingProgressbarResponse3 } from 'src/app/models/crowdfunding/progressbar.model';
import { parseApiDateToUtc } from 'src/app/utils/date-utils';
import { isPlatformBrowser } from '@angular/common';

interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

@Component({
    selector: 'app-riba-modal',
    templateUrl: './riba-modal.component.html',
    styleUrls: ['./riba-modal.component.scss'],
    standalone: false
})
export class RibaModalComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private countdownSubscription?: Subscription;
  countdown: CountdownTime = { days: 0, hours: 0, minutes: 0, seconds: 0 };
  
  isModalOpen = false;
  data: any = null;
  showRibaModalComponent = false;
  private fallbackEndDate = "2025-12-01 03:59:59";

  constructor(
    private ribaModalService: RibaModalService, 
    private typesenseService: TypesenseService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  private shouldShowPopupToday(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false; // SSR: do NOT show popup
    }
  
    const today = new Date().toISOString().split('T')[0];
    const lastShown = localStorage.getItem('ribaModalShownDate');
  
    if (lastShown === today) {
      return false;
    }
  
    localStorage.setItem('ribaModalShownDate', today);
    return true;
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // listens ALWAYS to modal open/close state
    this.ribaModalService.isModalOpen$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isOpen => {
        this.isModalOpen = isOpen;
      });

    this.typesenseService.getBannersVisibilityData()
      .pipe(
        takeUntil(this.destroy$),
        filter(res => !!res.popupBannerRiba),
        tap(() => this.showRibaModalComponent = this.shouldShowPopupToday()),
        filter(() => this.showRibaModalComponent),
        switchMap(() => this.typesenseService.fetchCrowdfundingProgressData3())
      )
      .subscribe(data => {
        this.data = data;
        const endDateString = this.data?.investment_end_date || this.fallbackEndDate;
        this.startTimer(parseApiDateToUtc(endDateString));
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeModal(): void {
    this.ribaModalService.closeModal();
  }

  private startTimer(endDate: Date): void {
    if (!isPlatformBrowser(this.platformId)) return;

      this.countdownSubscription?.unsubscribe(); 
  
      this.countdownSubscription = interval(1000)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          const now = new Date().getTime();
          const distance = endDate.getTime() - now;
  
          if (distance > 0) {
            this.countdown = {
              days: Math.floor(distance / (1000 * 60 * 60 * 24)),
              hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
              minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
              seconds: Math.floor((distance % (1000 * 60)) / 1000)
            };
          } else {
            this.countdown = { days: 0, hours: 0, minutes: 0, seconds: 0 };
            if (this.isModalOpen) this.closeModal();
            this.countdownSubscription?.unsubscribe();
          }
        });
    }

}